import { computed, ComputedRef, ref, WritableComputedRef } from 'vue';
import { useDistributionStore } from '@/features/Distribution/store';
import { useTariffStore } from '@/features/Tariffs/store';
import { useBrokerStore } from '@/features/Broker/store';
import { useCompaniesStore } from '@/features/Companies/store';
import {
  DISTRIBUTION_WAY,
  DistributionChannel,
  DistributionPool,
} from '@/features/Distribution/types';
import { VglError } from '@/services/errorHandler/vglError';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';

export interface UseDistributionSelection {
  selectedDistributionWay: WritableComputedRef<DISTRIBUTION_WAY>;
  selectedPoolId: WritableComputedRef<string | null>;
  selectedPool: ComputedRef<DistributionPool | undefined>;
  selectedChannelId: WritableComputedRef<string | null>;
  selectedChannel: ComputedRef<DistributionChannel | undefined>;

  isPoolingPossible: ComputedRef<boolean>;
  isDirectAgreementAvailableForCompany: ComputedRef<boolean>;
  availablePoolsForCompany: ComputedRef<DistributionPool[]>;
  preferredDefaultPool: ComputedRef<DistributionPool>;

  brokerIdForSelectedDistribution: ComputedRef<string | null>;
  brokerIdForCompany: ComputedRef<string | null>;

  isNotDBV: ComputedRef<boolean>;
  isDirectAgreementAllowedForBroker: ComputedRef<boolean>;

  isDistributedOverAnotherCompany: ComputedRef<boolean>;
  canSkipDistributionSelection: ComputedRef<boolean>;

  storeDistributionSelection: () => Promise<void>;
  setInsuranceId: (newInsuranceId: string) => void;

  possibleDistributions: ComputedRef<{
    showPooling: boolean;
    showErstantrag: boolean;
    showDifferentCompanyDistribution: boolean;
    canSkipDistributionSelection: boolean;
  }>;

  hasPossibleDistributionChannels: ComputedRef<boolean>;
}

export const useDistributionSelection = (
  actualInsuranceId: string,
): UseDistributionSelection => {
  const distributionStore = useDistributionStore();
  const insuranceId = ref(actualInsuranceId);

  const selectedDistributionWay = computed({
    get: () => distributionStore.data[insuranceId.value]?.selection.method,
    set: (v: DISTRIBUTION_WAY) => {
      distributionStore.data[insuranceId.value].selection.method = v;
    },
  });

  const availablePoolsForCompany = computed(
    () => distributionStore.data[insuranceId.value]?.pools ?? [],
  );

  const isDirectAgreementAvailableForCompany = computed(
    () =>
      useCompaniesStore().byInsuranceId[insuranceId.value].isDirektvereinbarung,
  );

  const isNotDBV = computed(() => insuranceId.value !== '72');

  const preferredDefaultPool = computed(
    () =>
      availablePoolsForCompany.value.find((pool) => pool.preferred) ||
      availablePoolsForCompany.value[0],
  );

  const isDirectAgreementAllowedForBroker = computed(
    () =>
      useBrokerStore().canCreateInitialApplication &&
      isDirectAgreementAvailableForCompany.value,
  );

  const selectedPoolId = computed({
    get: () => {
      const { pool } = distributionStore.data[insuranceId.value].selection;
      if (pool !== null && pool !== '') {
        return pool;
      }
      return null;
    },
    set: (v: string | null) => {
      distributionStore.data[insuranceId.value].selection.pool = v;
    },
  });

  const selectedPool = computed(() => {
    if (selectedPoolId.value === null) return undefined;

    return distributionStore.getSelectedPool(
      insuranceId.value,
      selectedPoolId.value,
    );
  });

  const selectedChannelId = computed({
    get: () => distributionStore.data[insuranceId.value].selection.channel,
    set: (v: string | null) => {
      if (v !== null) {
        selectedDistributionWay.value = DISTRIBUTION_WAY.USE_RELATED_COMPANY;
      }
      distributionStore.data[insuranceId.value].selection.channel = v;
    },
  });

  const selectedChannel = computed(() => {
    if (selectedChannelId.value === null) return undefined;

    return distributionStore.getSelectedChannel(
      insuranceId.value,
      selectedChannelId.value,
    );
  });

  const brokerIdForSelectedDistribution = computed(() => {
    if (selectedPoolId.value !== null) {
      return distributionStore.getBrokerIdForPool(
        insuranceId.value,
        selectedPoolId.value,
      );
    }
    const channel =
      selectedChannelId.value !== null
        ? selectedChannelId.value
        : insuranceId.value;
    return distributionStore.getBrokerIdForChannel(insuranceId.value, channel);
  });

  const isDistributedOverAnotherCompany = computed(() => {
    if (
      !selectedPool.value &&
      selectedChannel.value &&
      selectedChannel.value.insuranceId === insuranceId.value
    ) {
      return false;
    }

    return true;
  });

  const isPoolingPossible = computed(() => {
    const tariff = useTariffStore().recommendedTariff;

    if (tariff !== null) {
      return (
        !tariff.risikoDemver &&
        (!tariff.demvRabattiert || tariff.allgemeinerRabatt) &&
        availablePoolsForCompany.value.length > 0
      );
    }

    return availablePoolsForCompany.value.length > 0;
  });

  const storeDistributionSelection = async () => {
    if (
      selectedPoolId.value !== null &&
      selectedDistributionWay.value === DISTRIBUTION_WAY.USE_POOL
    ) {
      const pool = distributionStore.getSelectedPool(
        insuranceId.value,
        selectedPoolId.value,
      );

      if (pool) {
        await distributionStore.callPoolingCallback(insuranceId.value);
      } else {
        throw new VglError(
          VGL_ERROR_CODE.DISTRIBUTION_SELECTED_POOL_NOT_FOUND,
          {
            additionalErrorMessage: `- pool: ${selectedPoolId.value}`,
          },
        );
      }
    }

    if (
      distributionStore.hasBrokerIdOnDifferentChannel(insuranceId.value) &&
      selectedChannelId.value !== null &&
      selectedChannelId.value !== '' &&
      selectedChannelId.value !== insuranceId.value
    ) {
      await distributionStore.saveChannelSelection(insuranceId.value);
    }
  };

  const canSkipDistributionSelection = computed(() => {
    if (selectedChannelId.value !== null) {
      return distributionStore.hasBrokerIdForChannel(
        insuranceId.value,
        selectedChannelId.value,
      );
    }

    return false;
  });

  const setInsuranceId = (newInsuranceId: string) => {
    insuranceId.value = newInsuranceId;
  };

  const brokerIdForCompany = computed(() =>
    distributionStore.getBrokerIdForChannel(
      insuranceId.value,
      insuranceId.value,
    ),
  );

  const hasDifferentCompanyDistribution = computed(() =>
    distributionStore.hasBrokerIdOnDifferentChannel(insuranceId.value),
  );

  const possibleDistributions = computed(() => ({
    showPooling: isPoolingPossible.value,
    showErstantrag:
      isDirectAgreementAllowedForBroker.value &&
      isDirectAgreementAvailableForCompany.value &&
      isNotDBV.value,
    showDifferentCompanyDistribution:
      hasDifferentCompanyDistribution.value || !isNotDBV.value,
    canSkipDistributionSelection: canSkipDistributionSelection.value,
  }));

  const hasPossibleDistributionChannels = computed(() => {
    try {
      return (
        possibleDistributions.value.showDifferentCompanyDistribution ||
        possibleDistributions.value.showPooling ||
        possibleDistributions.value.showErstantrag ||
        brokerIdForCompany.value !== null
      );
    } catch (e) {
      return false;
    }
  });

  return {
    selectedDistributionWay,
    selectedPoolId,
    selectedPool,
    selectedChannelId,
    selectedChannel,

    isPoolingPossible,
    isDirectAgreementAvailableForCompany,
    availablePoolsForCompany,
    preferredDefaultPool,

    brokerIdForSelectedDistribution,
    brokerIdForCompany,

    isNotDBV,
    isDirectAgreementAllowedForBroker,

    isDistributedOverAnotherCompany,
    canSkipDistributionSelection,

    storeDistributionSelection,
    setInsuranceId,

    possibleDistributions,
    hasPossibleDistributionChannels,
  };
};
